// Config for the Commerce admin screens (backend doc sections 5.2-5.11).
// Resource types: table (list + optional create/edit/delete/row actions), form (load + save),
// action (standalone request, shows result), json (read-only report).
const t = (name, label, type = "text", extra = {}) => ({ name, label, type, ...extra });
const num = (n, l, x) => t(n, l, "number", x);
const chk = (n, l) => t(n, l, "checkbox");
const sel = (n, l, options) => t(n, l, "select", { options });
const list = (n, l) => t(n, l, "list");
const json = (n, l) => t(n, l, "json");
const note = t("note", "Note");
const range = [t("from", "From", "date"), t("to", "To", "date")];

const crud = (title, base, fields, extra = {}) => ({
  title,
  type: "table",
  url: base,
  create: { url: base, fields },
  edit: { method: "PUT", url: `${base}/{id}`, fields },
  del: { url: `${base}/{id}` },
  ...extra,
});

const report = (title, url, extra = {}) => ({ title, type: "json", url, params: range, ...extra });

export const commerceModules = {
  integrations: {
    title: "Payments & Integrations",
    resources: [
      {
        title: "Payment gateways",
        type: "table",
        url: "payment-gateways",
        create: {
          url: "payment-gateways",
          fields: [
            t("companyId", "Company id", "number", { default: "$companyId" }),
            sel("provider", "Provider", ["RAZORPAY", "CASHFREE"]),
            sel("mode", "Mode", ["TEST", "LIVE"]),
            t("label", "Label"),
            t("keyId", "Key id"),
            t("keySecret", "Key secret", "password"),
            t("webhookSecret", "Webhook secret", "password"),
            list("enabledMethods", "Enabled methods (comma)"),
            num("priority", "Priority"),
          ],
        },
        edit: {
          method: "PATCH",
          url: "payment-gateways/{id}",
          fields: [t("label", "Label"), list("enabledMethods", "Enabled methods (comma)"), num("priority", "Priority"), sel("status", "Status", ["ACTIVE", "DISABLED"])],
        },
        actions: [
          { label: "Rotate secrets", method: "PUT", url: "payment-gateways/{id}/secrets", fields: [t("keySecret", "Key secret", "password"), t("webhookSecret", "Webhook secret", "password")] },
          { label: "Test", method: "POST", url: "payment-gateways/{id}/test" },
        ],
      },
      {
        title: "Storage accounts",
        type: "table",
        url: "storage-accounts",
        create: {
          url: "storage-accounts",
          fields: [
            sel("provider", "Provider", ["S3", "MINIO"]),
            t("label", "Label"),
            t("endpoint", "Endpoint (MinIO)"),
            t("region", "Region"),
            t("bucket", "Bucket"),
            chk("pathStyle", "Path style"),
            t("publicBaseUrl", "Public base URL"),
            t("accessKeyId", "Access key id"),
            t("secretKey", "Secret key", "password"),
            chk("makeDefault", "Make default"),
          ],
        },
        edit: {
          method: "PATCH",
          url: "storage-accounts/{id}",
          fields: [t("label", "Label"), t("endpoint", "Endpoint"), t("region", "Region"), t("bucket", "Bucket"), chk("pathStyle", "Path style"), t("publicBaseUrl", "Public base URL"), chk("makeDefault", "Make default"), sel("status", "Status", ["ACTIVE", "DISABLED"])],
        },
        actions: [
          { label: "Rotate secret", method: "PUT", url: "storage-accounts/{id}/secret", fields: [t("accessKeyId", "Access key id"), t("secretKey", "Secret key", "password")] },
          { label: "Test", method: "POST", url: "storage-accounts/{id}/test" },
        ],
      },
      { title: "Invoicing queue", type: "json", url: "invoicing/queue", run: { label: "Retry failed", method: "POST", url: "invoicing/retry-failed" } },
    ],
  },

  coupons: {
    title: "Coupons",
    resources: [
      crud("Coupons", "coupon", [
        t("code", "Code"),
        t("description", "Description"),
        sel("type", "Type", ["PERCENT", "FIXED"]),
        num("value", "Value"),
        num("maxDiscount", "Max discount"),
        num("minOrderValue", "Min order value"),
        t("startsAt", "Starts at", "datetime-local"),
        t("endsAt", "Ends at", "datetime-local"),
        num("usageLimit", "Usage limit"),
        num("perCustomerLimit", "Per customer limit"),
        list("productIds", "Product ids (comma)"),
        list("categoryIds", "Category ids (comma)"),
        chk("active", "Active"),
      ]),
    ],
  },

  orderRequests: {
    title: "Order Requests",
    resources: [
      {
        title: "Requests",
        type: "table",
        url: "order-request",
        params: [sel("status", "Status", ["", "REQUESTED", "APPROVED", "REJECTED", "COMPLETED", "WITHDRAWN"])],
        actions: [
          { label: "Approve", method: "POST", url: "order-request/{id}/approve", fields: [note] },
          { label: "Reject", method: "POST", url: "order-request/{id}/reject", fields: [t("note", "Reason")] },
          { label: "Complete", method: "POST", url: "order-request/{id}/complete", fields: [chk("restock", "Put goods back on shelf"), note] },
        ],
      },
    ],
  },

  b2b: {
    title: "B2B",
    resources: [
      crud("Groups", "b2b/group", [t("name", "Name"), t("description", "Description"), num("discountPercent", "Discount %"), num("minOrderValue", "Min order value"), chk("active", "Active")]),
      {
        title: "Accounts",
        type: "table",
        url: "b2b/account",
        params: [sel("status", "Status", ["", "PENDING", "APPROVED", "REJECTED", "SUSPENDED"])],
        actions: [
          { label: "Decide", method: "PUT", url: "b2b/account/{id}/decision", fields: [sel("status", "Status", ["APPROVED", "REJECTED", "SUSPENDED"]), note, num("groupId", "Group id"), chk("clearGroup", "Clear group"), num("creditLimit", "Credit limit"), num("creditDays", "Credit days"), num("minOrderValue", "Min order value")] },
          { label: "Details", method: "GET", url: "b2b/account/{id}" },
          { label: "Credit", method: "GET", url: "b2b/account/{id}/credit" },
          { label: "Requests", method: "GET", url: "b2b/account/{id}/request" },
        ],
      },
      { title: "Settle credit", type: "action", method: "POST", url: "b2b/credit/{id}/settle", fields: [num("id", "Credit entry id")] },
      {
        title: "Prices",
        type: "table",
        url: "b2b/price",
        params: [num("groupId", "Group id"), num("accountId", "Account id"), num("productId", "Product id")],
        create: { url: "b2b/price", fields: [num("groupId", "Group id"), num("accountId", "Account id"), num("productId", "Product id"), num("minQuantity", "Min quantity"), num("unitPrice", "Unit price")] },
        del: { url: "b2b/price/{id}" },
      },
      { title: "Restrictions", type: "action", method: "PUT", url: "b2b/restriction", fields: [num("groupId", "Group id"), num("accountId", "Account id"), list("productIds", "Allowed product ids (comma)")] },
      { title: "Run repeat orders", type: "action", method: "POST", url: "b2b/repeat/run", fields: [] },
    ],
  },

  loyalty: {
    title: "Loyalty & Wallet",
    resources: [
      {
        title: "Settings",
        type: "form",
        url: "loyalty/settings",
        save: { method: "PUT", url: "loyalty/settings" },
        fields: [chk("pointsActive", "Points active"), num("earnPer100", "Earn per 100"), num("pointValue", "Point value"), num("minRedeemPoints", "Min redeem points"), num("maxRedeemPercent", "Max redeem %"), num("expiryDays", "Expiry days"), chk("clearExpiry", "Clear expiry"), chk("walletActive", "Wallet active"), chk("referralActive", "Referral active"), sel("referralRewardType", "Referral reward", ["POINTS", "WALLET"]), num("referrerReward", "Referrer reward"), num("refereeReward", "Referee reward"), num("referralMinOrder", "Referral min order"), num("referralMaxPerReferrer", "Max per referrer"), chk("clearReferralMax", "Clear max per referrer")],
      },
      crud("Tiers", "loyalty/tier", [t("name", "Name"), num("minLifetimePoints", "Min lifetime points"), num("earnMultiplier", "Earn multiplier")]),
      { title: "Customer loyalty", type: "action", method: "GET", url: "loyalty/customer/{customerId}", fields: [num("customerId", "Customer id")] },
      { title: "Adjust points", type: "action", method: "POST", url: "loyalty/adjust", fields: [num("customerId", "Customer id"), num("points", "Points (+/-)"), t("note", "Note")] },
      { title: "Customer wallet", type: "action", method: "GET", url: "wallet/customer/{customerId}", fields: [num("customerId", "Customer id")] },
      { title: "Credit wallet", type: "action", method: "POST", url: "wallet/credit", fields: [num("customerId", "Customer id"), num("amount", "Amount"), t("reason", "Reason"), t("note", "Note")] },
      { title: "Debit wallet", type: "action", method: "POST", url: "wallet/debit", fields: [num("customerId", "Customer id"), num("amount", "Amount"), t("note", "Note")] },
      {
        title: "Referrals",
        type: "table",
        url: "referral",
        params: [t("status", "Status")],
        actions: [{ label: "Void", method: "POST", url: "referral/{id}/void" }],
      },
    ],
  },

  subscriptions: {
    title: "Subscriptions",
    resources: [
      { title: "Summary", type: "json", url: "subscribe/admin/summary", run: { label: "Run renewals now", method: "POST", url: "subscribe/admin/run" } },
      {
        title: "Settings",
        type: "form",
        url: "subscribe/admin/settings",
        save: { method: "PUT", url: "subscribe/admin/settings" },
        fields: [chk("active", "Active"), num("graceDays", "Grace days"), num("maxFailures", "Max failures")],
      },
      crud("Offers", "subscribe/admin/offer", [num("productId", "Product id"), list("intervalsDays", "Intervals in days (comma)"), num("discountPercent", "Discount %"), num("maxQuantity", "Max quantity"), chk("active", "Active"), chk("clearMaxQuantity", "Clear max quantity")]),
      {
        title: "Subscriptions",
        type: "table",
        url: "subscribe/admin/subscription",
        params: [sel("status", "Status", ["", "ACTIVE", "PAUSED", "CANCELLED", "ENDED"])],
        actions: [
          { label: "Pause", method: "POST", url: "subscribe/admin/subscription/{id}/pause" },
          { label: "Resume", method: "POST", url: "subscribe/admin/subscription/{id}/resume" },
          { label: "Cancel", method: "POST", url: "subscribe/admin/subscription/{id}/cancel" },
          { label: "Details", method: "GET", url: "subscribe/admin/subscription/{id}" },
        ],
      },
      { title: "Cycles", type: "table", url: "subscribe/admin/cycle", params: [sel("status", "Status", ["", "DUE", "CHECKOUT", "ORDERED", "SKIPPED", "EXPIRED", "FAILED"])] },
    ],
  },

  content: {
    title: "Content & SEO",
    resources: [
      crud("Pages", "content/admin/page", [t("slug", "Slug"), t("title", "Title"), t("body", "Body", "textarea"), sel("status", "Status", ["DRAFT", "PUBLISHED"]), t("publishFrom", "Publish from", "datetime-local"), t("publishTo", "Publish to", "datetime-local"), chk("inNavigation", "In navigation"), num("sortOrder", "Sort order"), json("seo", "SEO (JSON)")]),
      crud(
        "Sections",
        "content/admin/section",
        [t("code", "Code"), t("title", "Title"), t("subtitle", "Subtitle"), sel("kind", "Kind", ["PRODUCTS", "CATEGORIES", "BANNERS", "LINKS", "TEXT"]), t("placement", "Placement"), num("position", "Position"), t("text", "Text", "textarea"), num("maxItems", "Max items"), chk("active", "Active"), t("startsAt", "Starts at", "datetime-local"), t("endsAt", "Ends at", "datetime-local")],
        { actions: [{ label: "Set items", method: "PUT", url: "content/admin/section/{id}/items", bodyField: "items", fields: [json("items", "Items JSON: [{refId,label,imageUrl,linkUrl}]")] }] }
      ),
      { title: "Featured products", type: "action", method: "PUT", url: "content/admin/featured/products", fields: [list("ids", "Product ids (comma)")] },
      { title: "Featured categories", type: "action", method: "PUT", url: "content/admin/featured/categories", fields: [list("ids", "Category ids (comma)")] },
      {
        title: "SEO records",
        type: "table",
        url: "content/admin/seo",
        create: { url: "content/admin/seo", method: "PUT", fields: [sel("entityType", "Entity type", ["PRODUCT", "CATEGORY", "BLOG", "HOME"]), num("entityId", "Entity id"), json("seo", "SEO JSON {metaTitle,metaDescription,canonicalUrl,noIndex,ogTitle,ogDescription,ogImage,ogType}")] },
        del: { url: "content/admin/seo/{id}" },
      },
    ],
  },

  storeAdmin: {
    title: "Store Admin",
    resources: [
      {
        title: "Configuration",
        type: "form",
        url: "storeadmin/config",
        save: { method: "PUT", url: "storeadmin/config" },
        fields: [t("storeName", "Store name"), t("tagline", "Tagline"), t("supportEmail", "Support email"), t("supportPhone", "Support phone"), t("storefrontUrl", "Storefront URL"), t("logoUrl", "Logo URL"), t("currency", "Currency"), chk("guestCheckout", "Guest checkout"), chk("checkoutPaused", "Checkout paused"), t("pauseMessage", "Pause message")],
      },
      crud("Channels", "storeadmin/channel", [t("code", "Code"), t("name", "Name"), t("description", "Description"), chk("active", "Active"), num("sortOrder", "Sort order")]),
      crud("Checkout rules", "storeadmin/rule", [sel("type", "Type", ["MIN_ORDER_VALUE", "MAX_ORDER_VALUE", "MAX_QUANTITY_PER_PRODUCT", "MAX_LINES"]), num("productId", "Product id (quantity rule)"), num("value", "Value"), t("message", "Message"), chk("active", "Active")]),
      { title: "Search customers", type: "action", method: "GET", url: "storeadmin/customer?q={q}", fields: [t("q", "Name, e-mail or phone (min 2)")] },
      { title: "Customer overview", type: "action", method: "GET", url: "storeadmin/customer/{id}", fields: [num("id", "Customer id")] },
      { title: "Block / flag customer", type: "action", method: "PUT", url: "storeadmin/customer/{id}/flag", fields: [num("id", "Customer id"), chk("blocked", "Blocked"), t("blockReason", "Block reason"), t("tags", "Tags"), t("adminNote", "Admin note")] },
      { title: "Flagged customers", type: "table", url: "storeadmin/customer-flag", params: [sel("blocked", "Blocked", ["", "true", "false"])] },
      {
        title: "Orders",
        type: "table",
        url: "storeadmin/order",
        pick: "items",
        params: [t("status", "Status"), num("customerId", "Customer id"), t("from", "From", "date"), t("to", "To", "date"), num("page", "Page"), num("size", "Size")],
        actions: [{ label: "Details", method: "GET", url: "storeadmin/order/{cartBundleId}" }],
      },
      { title: "Add order note", type: "action", method: "POST", url: "storeadmin/order/{cartBundleId}/note", fields: [num("cartBundleId", "Cart bundle id"), t("note", "Note")] },
      report("Promotions", "storeadmin/promotion"),
    ],
  },

  analytics: {
    title: "Analytics",
    resources: [
      report("Overview", "analytics/admin/overview"),
      report("Daily", "analytics/admin/daily", { type: "table" }),
      report("Funnel", "analytics/admin/funnel"),
      report("Products", "analytics/admin/products", { type: "table", params: [...range, sel("sort", "Sort", ["revenue", "views", "units"]), num("limit", "Limit")] }),
      report("Searches", "analytics/admin/searches", { params: [num("limit", "Limit")] }),
      report("Customers", "analytics/admin/customers"),
      report("Channels", "analytics/admin/channels"),
      report("Campaigns", "analytics/admin/campaigns"),
      report("Coupons", "analytics/admin/coupons"),
      { title: "Purge old events", type: "action", method: "POST", url: "analytics/admin/purge", fields: [] },
    ],
  },
};

export const commerceNav = Object.entries(commerceModules).map(([key, m], i) => ({
  id: `c${i}`,
  title: m.title,
  link: `/commerce/${key}`,
}));
